<template>
  <div class="controlled-tour-map">
    <div class="actions flex justify-between mb-10">
      <div class="flex gap-x-5">
        <Switch v-model="showStartpoints" :label="$t('pages.dashboard.overview.showStartpoints')" />
        <Switch v-model="showFullTours" :label="$t('pages.dashboard.overview.showFullTours')" />
        <Switch v-model="showEndpoints" :label="$t('pages.dashboard.overview.showEndpoints')" />
      </div>
      <div class="flex gap-x-5">
        <div class="flex flex-col">
          <FormInput
            v-model="displayedLinesThreshold"
            :label="$t('pages.dashboard.overview.displayedLinesThreshold')"
            :min="0"
            class="w-full"
            type="number"
          />
        </div>
        <div v-if="hasCampaignFilter" class="region items-end flex">
          <Multiselect
            v-model="selectedCampaign"
            :options="campaignList"
            :label="$t('campaign.DEFAULT')"
            :can-deselect="true"
            :can-clear="true"
            class="w-full"
            :searchable="true"
            mode="multiple"
            :hide-selected="false"
            @select="onCampaignSelect"
          />
        </div>
        <div class="flex flex-col">
          <div v-if="hasEmbeddedDateRange" class="flex items-end">
            <DateRange
              v-model="dateRange"
              :label="$t('campaign.dateRangeRestricted')"
              mode="datetime"
            />
            <ClearIcon v-if="dateRangeRestriction === false" class="w-6 h-6 mb-2 ml-2 cursor-pointer" @click="clearDateRange()" />
          </div>
          <div v-if="dateRangeRestriction !== false" class="text-pane-label mt-2">
            {{ $t('campaign.dateRangeRestrictedText') }}
          </div>
        </div>
        <custom-button v-if="lazy" variant="primary" @click="refetchPositions()">
          {{ $t('pages.dashboard.overview.reloadMap') }}
        </custom-button>
      </div>
    </div>
    <TourMap
      ref="tourMap"
      :positions="positions ?? []"
      :busy="loadingPositions"
      :show-endpoints="showEndpoints"
      :show-startpoints="showStartpoints"
      :show-tour="showFullTours"
      :auto-center="autoCenter"
      :show-last-known-postition="showLastKnownPosition"
      :initial-zoom="zoom ?? initialZoom"
      :displayed-lines-threshold="displayedLinesThresholdKm"
      @bounds="updateBounds"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import FormInput from '@utils/inputs/Input.vue';
import { useI18n } from 'vue-i18n';
import Switch from '@/components/utils/inputs/Switch.vue';
import Multiselect from '@/components/utils/Multiselect.vue';
import DateRange from '@/components/utils/inputs/DateRange.vue';
import ClearIcon from '@/assets/images/remove_filter.svg?component';
import type { CampaignGroup, Position } from '@/entities/Entities';
import { type DateRangeFilter, type Filter, useProvider } from '../table/hooks/provider';
import TourMap from './TourMap.vue';
import { defaultCenter } from '@/utils/map';
import { useNotify } from '../notifications';

interface Props {
  filter: Filter;
  lazy?: boolean;
  hasCampaignFilter?: boolean;
  campaignGroup?: CampaignGroup;
  hasEmbeddedDateRange?: boolean;
  dateRangeRestriction?: boolean | { start: Date; end: Date };
  autoCenter?: boolean;
  disabled?: boolean;
  useBounds?: boolean;
  center?: google.maps.LatLngLiteral;
  showLastKnownPosition?: boolean;
  initialZoom?: number;
}

const props = withDefaults(defineProps<Props>(), {
  lazy: true,
  center: () => defaultCenter,
  useBounds: true,
  dateRangeRestriction: false,
});

const notify = useNotify();

const { t } = useI18n();

const tourMap = ref();

const showStartpoints = ref(false);
const showEndpoints = ref(false);
const showFullTours = ref(true);
const bounds = ref();
const selectedCampaign = ref();

const zoom = ref();

const dateRange = ref({
  start: props.dateRangeRestriction?.start ?? undefined,
  end: props.dateRangeRestriction?.end ?? undefined,
});

const displayedLinesThreshold = ref(100);
const displayedLinesThresholdKm = ref(0.1);

watch(displayedLinesThreshold, (newValue) => {
  if (!Number.isNaN(newValue) && newValue > 0) {
    // only update the km value if the input is a valid number
    displayedLinesThresholdKm.value = newValue / 1000;
  }
});

const onCampaignSelect = (campaignId) => {
  const campaign = props.campaignGroup?.campaigns?.find((c) => c.id === campaignId);
  if (campaign) {
    const newStart = new Date(campaign.actualStartDate);
    const newEnd = new Date(campaign.actualEndDate);
    // if already set, only extend the range, not restrict it
    if (!dateRange.value.start || dateRange.value.start > newStart) {
      dateRange.value.start = newStart;
    }
    if (!dateRange.value.end || dateRange.value.end < newEnd) {
      dateRange.value.end = newEnd;
    }
  }
};

const clearDateRange = () => {
  dateRange.value = {
    start: undefined,
    end: undefined,
  };
};

const campaignList = computed(() => props.campaignGroup?.campaigns?.map((c) => ({ label: c?.name, value: c.id })));

const updateBounds = (newBounds: google.maps.LatLngLiteral) => {
  bounds.value = newBounds;
};

const getPositionFilters = computed(() => ({
  filter: {
    ...(props.hasEmbeddedDateRange ? (dateRange.value?.start && dateRange.value?.end ? {
      tour_createdAt: { end: dateRange.value?.end, start: dateRange.value?.start } as DateRangeFilter,
    } : {}) : {
      tour_createdAt: props.filter.date ?? props.filter.createdAt as DateRangeFilter,
    }),
    tour_driver_region: props.filter.region,
    tour_campaignRegistration_campaign_group: props.filter.campaignGroup,
    tour_driver: props.filter?.driver,
    tour_campaignRegistration_campaigns: selectedCampaign.value,
    tour_license_exists: props.filter['exists[license]'],
    tour_campaignRegistration_exists: props.filter['exists[campaignRegistration]'],
    showFullTours: showFullTours.value,
    ...(bounds.value && props.useBounds
      ? {
        ne: `${bounds.value.north}|${bounds.value.east}`,
        sw: `${bounds.value.south}|${bounds.value.west}`,
      }
      : {}),
  },
}));

const {
  data: positions,
  loading: loadingPositions,
  refetch,
  error,
} = useProvider<Position[]>('/tours/positions', getPositionFilters.value, {
  immediate: (!props.lazy && !props.hasCampaignFilter) && !props.disabled,
});

const refetchPositions = (filters = null) => {
  if (props.hasCampaignFilter) {
    const location = props.campaignGroup?.campaigns?.find((c) => c.id === selectedCampaign.value)?.region?.gisLocation;
    tourMap.value?.moveToCenter(location?.lat, location?.long);
    zoom.value = 13;
  }
  refetch(filters || getPositionFilters.value);
};

const moveToCenter = (lat: number, long: number) => {
  tourMap.value?.moveToCenter(lat, long);
};

watch(error, () => {
  if (error.value && !loadingPositions.value) {
    notify?.({
      variant: 'error',
      title: t('tourMap.error'),
    });
  }
});

defineExpose({
  moveToCenter,
});

watch(
  getPositionFilters,
  async (filters, oldFilters) => {
    if (props.lazy || props.disabled) return;
    if (isEqual(filters, oldFilters)) return;
    if (props.hasCampaignFilter && !(selectedCampaign.value && selectedCampaign.value.length > 0 && dateRange.value?.start && dateRange.value?.end)) return;
    refetchPositions(filters);
  },
);

watch(loadingPositions, (newValue) => {
  if (newValue) tourMap.value?.clearMap();
});
</script>

<style lang="scss" scoped>
.region {
  width: 20rem;
}
</style>
